import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Target, Clock, Activity } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ScoreSlider from "@/components/ScoreSlider";

const STAGES = [
  "I think I'm in perimenopause",
  "I'm in menopause",
  "I'm postmenopausal",
  "I'm not sure",
  "I'd rather not say",
];
const PRIORITIES = [
  "Sleep", "Hot flashes/night sweats", "Energy/fatigue", "Brain fog/concentration",
  "Mood/stress", "Body changes", "Movement/fitness", "Nutrition",
  "Vaginal/genitourinary symptoms", "Something else",
];
const MINUTES = ["5 minutes", "10 minutes", "15 minutes", "20+ minutes"];
const ACTIVITY = ["Mostly inactive", "Some walking/movement", "Regularly active", "Very active", "It varies"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [stage, setStage] = useState("");
  const [priorities, setPriorities] = useState([]);
  const [minutes, setMinutes] = useState("");
  const [activity, setActivity] = useState("");
  const [goal, setGoal] = useState("");
  const [sleep, setSleep] = useState(5);
  const [energy, setEnergy] = useState(5);
  const [mood, setMood] = useState(5);
  const [impact, setImpact] = useState(5);

  const togglePriority = (p) => {
    setPriorities((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const TOTAL = 9;
  const canNext = () => {
    switch (step) {
      case 0: return !!stage;
      case 1: return priorities.length > 0;
      case 2: return !!minutes;
      case 3: return !!activity;
      case 4: return goal.trim().length > 0;
      default: return true;
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", {
        menopause_stage: stage,
        priorities,
        daily_minutes: minutes,
        activity_level: activity,
        goal,
        sleep_score: sleep,
        energy_score: energy,
        mood_score: mood,
        life_impact_score: impact,
      });
      await refresh();
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step < TOTAL - 1) setStep(step + 1);
    else submit();
  };

  if (result) {
    return (
      <div className="min-h-screen bg-brand flex items-center justify-center px-6" data-testid="onboarding-result">
        <div className="max-w-md w-full text-center animate-soft-scale">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "var(--amber)" }}>
            <Sparkles size={30} className="text-brand" />
          </div>
          <p className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase mt-6">Your reset is ready</p>
          <h1 className="font-serif text-4xl font-bold text-white mt-3">Welcome to Day 1</h1>

          <div className="mt-8 space-y-3 text-left">
            <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
              <div className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-wide">
                <Target size={14} /> Your focus
              </div>
              <div className="text-white text-lg font-semibold mt-1">{result.primary_priority}</div>
            </div>
            <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
              <div className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-wide">
                <Clock size={14} /> Your daily commitment
              </div>
              <div className="text-white text-lg font-semibold mt-1">{result.daily_minutes} a day</div>
            </div>
            <div className="rounded-2xl p-5" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
              <div className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-wide">
                <Activity size={14} /> Your starting wellbeing score
              </div>
              <div className="text-white text-3xl font-serif font-bold mt-1">{result.starting_score}<span className="text-lg text-white/50">/100</span></div>
              <div className="text-white/50 text-xs mt-1">Based on your self reported answers, not a medical assessment.</div>
            </div>
          </div>

          <button
            data-testid="start-day-1-btn"
            onClick={() => navigate("/home")}
            className="mt-8 w-full bg-white text-brand hover:bg-[var(--sage)] font-semibold py-4 rounded-full transition-colors"
          >
            Start Day 1
          </button>
        </div>
      </div>
    );
  }

  const OptionButton = ({ selected, onClick, children, testId }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all font-medium ${
        selected
          ? "border-[var(--brand)] bg-sage text-ink"
          : "border-subtle bg-white text-ink-soft hover:border-[var(--text-muted)]"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="onboarding-page">
      {/* progress */}
      <div className="max-w-md w-full mx-auto px-6 pt-8">
        <div className="flex items-center gap-3">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} data-testid="onboarding-back" className="text-ink-muted">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <div className="w-[22px]" />
          )}
          <div className="flex-1 h-2 rounded-full bg-[var(--surface-accent)] overflow-hidden">
            <div className="h-full bg-brand transition-all duration-300" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} />
          </div>
          <span className="text-xs font-semibold text-ink-muted">{step + 1}/{TOTAL}</span>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto px-6 py-8 flex-1">
        <div key={step} className="animate-fade-rise">
          {step === 0 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink">Where are you in your menopause journey?</h2>
              <div className="mt-6 space-y-3">
                {STAGES.map((s, i) => (
                  <OptionButton key={s} selected={stage === s} onClick={() => setStage(s)} testId={`stage-${i}`}>{s}</OptionButton>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink">What's bothering you most right now?</h2>
              <p className="text-ink-soft text-sm mt-2">Choose all that apply.</p>
              <div className="mt-6 space-y-3">
                {PRIORITIES.map((p, i) => (
                  <OptionButton key={p} selected={priorities.includes(p)} onClick={() => togglePriority(p)} testId={`priority-${i}`}>{p}</OptionButton>
                ))}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink">How much time can you realistically give yourself each day?</h2>
              <div className="mt-6 space-y-3">
                {MINUTES.map((m, i) => (
                  <OptionButton key={m} selected={minutes === m} onClick={() => setMinutes(m)} testId={`minutes-${i}`}>{m}</OptionButton>
                ))}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink">How would you describe your current activity level?</h2>
              <div className="mt-6 space-y-3">
                {ACTIVITY.map((a, i) => (
                  <OptionButton key={a} selected={activity === a} onClick={() => setActivity(a)} testId={`activity-${i}`}>{a}</OptionButton>
                ))}
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink">What's your biggest goal for the next 30 days?</h2>
              <textarea
                data-testid="goal-input"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                rows={4}
                className="mt-6 w-full rounded-2xl border border-subtle bg-white px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
                placeholder="e.g. Sleep through the night and feel less foggy at work"
              />
            </>
          )}
          {step === 5 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink mb-6">How are you sleeping at the moment?</h2>
              <ScoreSlider label="Sleep quality" value={sleep} onChange={setSleep} testId="onboarding-sleep" lowLabel="Very poor" highLabel="Excellent" />
            </>
          )}
          {step === 6 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink mb-6">How is your energy?</h2>
              <ScoreSlider label="Energy level" value={energy} onChange={setEnergy} testId="onboarding-energy" lowLabel="Drained" highLabel="Energised" />
            </>
          )}
          {step === 7 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink mb-6">How is your mood and stress?</h2>
              <ScoreSlider label="Mood / stress" value={mood} onChange={setMood} testId="onboarding-mood" lowLabel="Struggling" highLabel="Great" />
            </>
          )}
          {step === 8 && (
            <>
              <h2 className="font-serif text-2xl font-bold text-ink mb-6">How much is menopause affecting your everyday life?</h2>
              <ScoreSlider label="Daily impact" value={impact} onChange={setImpact} testId="onboarding-impact" lowLabel="Not at all" highLabel="A great deal" />
            </>
          )}
        </div>
      </div>

      <div className="max-w-md w-full mx-auto px-6 pb-10 sticky bottom-0 bg-app pt-2">
        <button
          data-testid="onboarding-next"
          disabled={!canNext() || busy}
          onClick={next}
          className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-4 rounded-full transition-colors disabled:opacity-40"
        >
          {busy ? "Preparing your reset…" : step === TOTAL - 1 ? "See my reset" : "Continue"}
        </button>
      </div>
    </div>
  );
}
